import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

export default function Adhesion() {
  const { user } = useAuth();
  // Stocker les adhésions par année : { SAR: { 2025: { status: 'actif' }, 2026: { status: 'en_attente' } }, AMAIR: {...}, ADAR: {...} }
  const [adhesionsParAnnee, setAdhesionsParAnnee] = useState({ SAR: {}, AMAIR: {}, ADAR: {} });
  const [loading, setLoading] = useState(true);
  const [anneesDisponibles, setAnneesDisponibles] = useState([]);
  const [selectedAnnee, setSelectedAnnee] = useState({
    SAR: null,
    AMAIR: null,
    ADAR: null
  });

  useEffect(() => {
    fetchAnneesDisponibles();
    if (user) {
      fetchAdhesions();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchAnneesDisponibles = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/parametres/annees-disponibles`
      );
      const annees = response.data.anneesDisponibles || [];
      setAnneesDisponibles(annees);
    } catch (error) {
      console.error('Erreur lors du chargement des années:', error);
    }
  };

  const fetchAdhesions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/adhesions/my-adhesions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Organiser les adhésions par organisme et par année
      const adhesions = { SAR: {}, AMAIR: {}, ADAR: {} };
      
      response.data.forEach(adhesion => {
        if (adhesion.status !== 'refusée' && adhesion.annee) {
          const organisme = adhesion.organisme;
          const annee = adhesion.annee;
          
          if (organisme === 'SAR' || organisme === 'AMAIR' || organisme === 'ADAR') {
            adhesions[organisme][annee] = {
              status: adhesion.status,
              isPending: ['en_attente', 'paiement_demande'].includes(adhesion.status),
              isActive: adhesion.status === 'actif',
              adhesionAMAIRGratuite: adhesion.adhesionAMAIRGratuite || false
            };
          }
        }
      });
      
      setAdhesionsParAnnee(adhesions);
    } catch (error) {
      console.error('Erreur lors de la récupération des adhésions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les années ouvertes pour un organisme (exclure celles où l'utilisateur a déjà une adhésion)
  const getAnneesOuvertes = (annees, organisme) => {
    return annees.filter(anneeData => {
      const org = anneeData.organismes.find(
        o => o.organisme === organisme && o.adhesionsOuvertes
      );
      if (!org) return false;
      
      // Exclure si l'utilisateur a déjà une adhésion pour cette année (sauf si refusée)
      const adhesionExistante = adhesionsParAnnee[organisme]?.[anneeData.annee];
      if (adhesionExistante) return false;
      
      return true;
    });
  };

  // Années où l'utilisateur a déjà une adhésion (pour affichage)
  const getAnneesAvecAdhesion = (organisme) => {
    return Object.entries(adhesionsParAnnee[organisme] || {})
      .filter(([annee, data]) => data.status !== 'abandonnee')
      .map(([annee, data]) => ({
        annee: parseInt(annee),
        ...data
      }));
  };

  const sarAnneesOuvertes = getAnneesOuvertes(anneesDisponibles, 'SAR');
  const amairAnneesOuvertes = getAnneesOuvertes(anneesDisponibles, 'AMAIR');
  const adarAnneesOuvertes = getAnneesOuvertes(anneesDisponibles, 'ADAR');
  const sarAdhesionsExistantes = getAnneesAvecAdhesion('SAR');
  const amairAdhesionsExistantes = getAnneesAvecAdhesion('AMAIR');
  const adarAdhesionsExistantes = getAnneesAvecAdhesion('ADAR');

  // Vérifier si une adhésion SAR en cours avec adhesionAMAIRGratuite bloque l'accès à AMAIR pour une année donnée
  const getSarAdhesionWithAMAIRGratuitePending = (annee) => {
    const sarAdhesion = adhesionsParAnnee.SAR?.[annee];
    if (sarAdhesion && sarAdhesion.adhesionAMAIRGratuite && sarAdhesion.isPending) {
      return sarAdhesion;
    }
    return null;
  };

  // Présélectionner l'année en cours si disponible, et resynchroniser si l'année sélectionnée n'est plus proposée
  useEffect(() => {
    const syncSelectedYear = (anneesOuvertes, currentSelection) => {
      if (anneesOuvertes.length === 0) return null;

      const isStillAvailable = anneesOuvertes.some(
        (a) => Number(a.annee) === Number(currentSelection)
      );
      if (currentSelection && isStillAvailable) {
        return currentSelection;
      }

      const anneeEnCours = anneesOuvertes.find((a) => a.estAnneeEnCours);
      return anneeEnCours?.annee || anneesOuvertes[0]?.annee || null;
    };

    setSelectedAnnee((prev) => {
      const nextSAR = syncSelectedYear(sarAnneesOuvertes, prev.SAR);
      const nextAMAIR = syncSelectedYear(amairAnneesOuvertes, prev.AMAIR);
      const nextADAR = syncSelectedYear(adarAnneesOuvertes, prev.ADAR);

      if (
        nextSAR === prev.SAR &&
        nextAMAIR === prev.AMAIR &&
        nextADAR === prev.ADAR
      ) {
        return prev;
      }

      return {
        SAR: nextSAR,
        AMAIR: nextAMAIR,
        ADAR: nextADAR,
      };
    });
  }, [anneesDisponibles, adhesionsParAnnee, sarAnneesOuvertes, amairAnneesOuvertes, adarAnneesOuvertes]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-semibold text-slate-900 sm:text-4xl">
            Choisissez votre groupement
          </h1>
          <p className="mt-4 text-lg text-slate-500">
            Sélectionnez le groupement auquel vous souhaitez adhérer ainsi que l'année d'adhésion choisie
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2">

          {/* Carte SAR */}
          <div className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
            <div className="p-8">
              <div className="text-center mb-6">
                <img src="/logos/logo_sar.png" alt="SAR" className="w-auto mx-auto h-32 mb-6" />
                <h2 className="text-2xl font-bold text-slate-900 mb-2">SAR</h2>
                <p className="text-sm text-slate-600 font-medium">
                  Syndicat Apicole de la Réunion
                </p>
              </div>
              
              <div className="mb-6">
                <p className="text-slate-700 text-sm leading-relaxed">
                  Syndicat catégoriel dédiée à la défense et à la promotion de l'apiculture.
                  Rejoignez une communauté engagée pour la promotion des abeilles le développement de l'apiculture.
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-honey-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-slate-600">Promotion et valorisation de l'apiculture</span>
                </div>
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-honey-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-slate-600">Défense des intérêts des apiculteurs</span>
                </div>
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-honey-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-slate-600">Événements et rencontres</span>
                </div>
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-honey-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-slate-600">Réseau ouvert à tous les apiculteurs : professionnels, de loisir ou en devenir</span>
                </div>
              </div>

              <Link
                to="/groupement/sar"
                className="block w-full text-center px-6 py-3 mb-3 text-base font-medium rounded-lg text-honey-500 hover:text-honey-600 bg-white border-2 border-honey-500 hover:border-honey-600 transition-colors"
              >
                En savoir plus sur le SAR
              </Link>

              {/* Afficher les adhésions existantes */}
              {sarAdhesionsExistantes.length > 0 && (
                <div className="space-y-2 mb-3">
                  {sarAdhesionsExistantes.map(adhesion => (
                    <div 
                      key={adhesion.annee}
                      className={`flex items-center justify-between px-4 py-2 rounded-lg ${
                        adhesion.isPending 
                          ? 'border-2 border-honey-500 bg-honey-50' 
                          : 'border-2 border-green-500 bg-green-50'
                      }`}
                    >
                      <span className="text-sm font-medium text-slate-700">
                        {adhesion.annee}
                      </span>
                      <div className="flex items-center">
                        {adhesion.isPending ? (
                          <>
                            <svg className="h-4 w-4 text-honey-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm font-medium text-honey-700">En attente</span>
                          </>
                        ) : (
                          <>
                            <svg className="h-4 w-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm font-medium text-green-700">Adhérent</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Option pour adhérer à une nouvelle année */}
              {sarAnneesOuvertes.length === 0 ? (
                sarAdhesionsExistantes.length === 0 && (
                  <div className="flex items-center justify-center px-6 py-3 border-2 border-slate-300 rounded-lg bg-slate-50">
                    <svg className="h-5 w-5 text-slate-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-base font-medium text-slate-500">Adhésions fermées</span>
                  </div>
                )
              ) : (
                <div className="space-y-3">
                  {/* Sélecteur d'année */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {sarAdhesionsExistantes.length > 0 ? 'Adhérer pour une autre année' : 'Année d\'adhésion'}
                    </label>
                    <select
                      value={selectedAnnee.SAR || ''}
                      onChange={(e) => setSelectedAnnee(prev => ({ ...prev, SAR: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
                    >
                      {sarAnneesOuvertes.map(anneeData => (
                        <option key={anneeData.annee} value={anneeData.annee}>
                          {anneeData.annee} {anneeData.estAnneeEnCours ? '(année en cours)' : ''}
                        </option>
                      ))}
                    </select>
                    {selectedAnnee.SAR && !sarAnneesOuvertes.find(a => a.annee === selectedAnnee.SAR)?.estAnneeEnCours && (
                      <p className="mt-1 text-xs text-honey-600">
                        Votre adhésion prendra effet le 1er janvier {selectedAnnee.SAR}
                      </p>
                    )}
                  </div>
                  <Link
                    to={`/adhesion/sar?annee=${selectedAnnee.SAR}`}
                    className="block w-full text-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-honey-500 hover:bg-honey-600 transition-colors"
                  >
                    Adhérer au SAR pour {selectedAnnee.SAR}
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Carte ADAR */}
          <div className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
            <div className="p-8">
              <div className="text-center mb-6">
                <img src="/logos/logo_adar.png" alt="ADAR" className="w-auto mx-auto h-32 mb-6" />
                <h2 className="text-2xl font-bold text-slate-900 mb-2">ADAR</h2>
                <p className="text-sm text-slate-600 font-medium">
                  Association pour le Développement de l'Apiculture à l'île de la Réunion
                </p>
              </div>
              
              <div className="mb-6">
                <p className="text-slate-700 text-sm leading-relaxed">
                  Association dédiée aux apiculteurs professionnels ou souhaitant le devenir.
                  Rejoignez une communauté d'apiculteurs indépendants.
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-honey-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-slate-600">Développement de la filière apicole réunionnaise</span>
                </div>
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-honey-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-slate-600">Structuration de la filière</span>
                </div>
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-honey-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-slate-600">Accompagnement technique</span>
                </div>
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-honey-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-slate-600">Projets collectifs</span>
                </div>
              </div>

              <Link
                to="/groupement/adar"
                className="block w-full text-center px-6 py-3 mb-3 text-base font-medium rounded-lg text-honey-500 hover:text-honey-600 bg-white border-2 border-honey-500 hover:border-honey-600 transition-colors"
              >
                En savoir plus sur l'ADAR
              </Link>

              {/* Afficher les adhésions existantes */}
              {adarAdhesionsExistantes.length > 0 && (
                <div className="space-y-2 mb-3">
                  {adarAdhesionsExistantes.map(adhesion => (
                    <div 
                      key={adhesion.annee}
                      className={`flex items-center justify-between px-4 py-2 rounded-lg ${
                        adhesion.isPending 
                          ? 'border-2 border-honey-500 bg-honey-50' 
                          : 'border-2 border-green-500 bg-green-50'
                      }`}
                    >
                      <span className="text-sm font-medium text-slate-700">
                        {adhesion.annee}
                      </span>
                      <div className="flex items-center">
                        {adhesion.isPending ? (
                          <>
                            <svg className="h-4 w-4 text-honey-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm font-medium text-honey-700">En attente</span>
                          </>
                        ) : (
                          <>
                            <svg className="h-4 w-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm font-medium text-green-700">Adhérent</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Option pour adhérer à une nouvelle année */}
              {adarAnneesOuvertes.length === 0 ? (
                adarAdhesionsExistantes.length === 0 && (
                  <div className="flex items-center justify-center px-6 py-3 border-2 border-slate-300 rounded-lg bg-slate-50">
                    <svg className="h-5 w-5 text-slate-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-base font-medium text-slate-500">Adhésions fermées</span>
                  </div>
                )
              ) : (
                <div className="space-y-3">
                  {/* Sélecteur d'année */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {adarAdhesionsExistantes.length > 0 ? 'Adhérer pour une autre année' : 'Année d\'adhésion'}
                    </label>
                    <select
                      value={selectedAnnee.ADAR || ''}
                      onChange={(e) => setSelectedAnnee(prev => ({ ...prev, ADAR: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
                    >
                      {adarAnneesOuvertes.map(anneeData => (
                        <option key={anneeData.annee} value={anneeData.annee}>
                          {anneeData.annee} {anneeData.estAnneeEnCours ? '(année en cours)' : ''}
                        </option>
                      ))}
                    </select>
                    {selectedAnnee.ADAR && !adarAnneesOuvertes.find(a => a.annee === selectedAnnee.ADAR)?.estAnneeEnCours && (
                      <p className="mt-1 text-xs text-honey-600">
                        Votre adhésion prendra effet le 1er janvier {selectedAnnee.ADAR}
                      </p>
                    )}
                  </div>
                  <Link
                    to={`/adhesion/adar?annee=${selectedAnnee.ADAR}`}
                    className="block w-full text-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-honey-500 hover:bg-honey-600 transition-colors"
                  >
                    Adhérer à l'ADAR pour {selectedAnnee.ADAR}
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Carte AMAIR */}
          <div className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
            <div className="p-8">
              <div className="text-center mb-6">
                <img src="/logos/logo_amair.png" alt="AMAIR" className="w-auto mx-auto h-32 mb-6" />
                <h2 className="text-2xl font-bold text-slate-900 mb-2">AMAIR</h2>
                <p className="text-sm text-slate-600 font-medium">
                  Association de la Maison de l'Apiculture de l'Île de la Réunion
                </p>
              </div>
              
              <div className="mb-6">
                <p className="text-slate-700 text-sm leading-relaxed">
                  Association d’apiculteurs passionnés par l’apiculture.
                  Participez au développement des offres des services aux apiculteurs.
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-honey-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-slate-600">Miellerie collective</span>
                </div>
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-honey-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-slate-600">Partage d'expériences</span>
                </div>
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-honey-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-slate-600">Formation et accompagnement</span>
                </div>
              </div>

              <Link
                to="/groupement/amair"
                className="block w-full text-center px-6 py-3 mb-3 text-base font-medium rounded-lg text-honey-500 hover:text-honey-600 bg-white border-2 border-honey-500 hover:border-honey-600 transition-colors"
              >
                En savoir plus sur l'AMAIR
              </Link>

              {/* Afficher les adhésions existantes */}
              {amairAdhesionsExistantes.length > 0 && (
                <div className="space-y-2 mb-3">
                  {amairAdhesionsExistantes.map(adhesion => (
                    <div 
                      key={adhesion.annee}
                      className={`flex items-center justify-between px-4 py-2 rounded-lg ${
                        adhesion.isPending 
                          ? 'border-2 border-honey-500 bg-honey-50' 
                          : 'border-2 border-green-500 bg-green-50'
                      }`}
                    >
                      <span className="text-sm font-medium text-slate-700">
                        {adhesion.annee}
                      </span>
                      <div className="flex items-center">
                        {adhesion.isPending ? (
                          <>
                            <svg className="h-4 w-4 text-honey-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm font-medium text-honey-700">En attente</span>
                          </>
                        ) : (
                          <>
                            <svg className="h-4 w-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm font-medium text-green-700">Adhérent</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Option pour adhérer à une nouvelle année */}
              {amairAnneesOuvertes.length === 0 ? (
                amairAdhesionsExistantes.length === 0 && (
                  <div className="flex items-center justify-center px-6 py-3 border-2 border-slate-300 rounded-lg bg-slate-50">
                    <svg className="h-5 w-5 text-slate-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-base font-medium text-slate-500">Adhésions fermées</span>
                  </div>
                )
              ) : (
                <div className="space-y-3">
                  {/* Sélecteur d'année */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {amairAdhesionsExistantes.length > 0 ? 'Adhérer pour une autre année' : 'Année d\'adhésion'}
                    </label>
                    <select
                      value={selectedAnnee.AMAIR || ''}
                      onChange={(e) => setSelectedAnnee(prev => ({ ...prev, AMAIR: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
                    >
                      {amairAnneesOuvertes.map(anneeData => (
                        <option key={anneeData.annee} value={anneeData.annee}>
                          {anneeData.annee} {anneeData.estAnneeEnCours ? '(année en cours)' : ''}
                        </option>
                      ))}
                    </select>
                    {selectedAnnee.AMAIR && !amairAnneesOuvertes.find(a => a.annee === selectedAnnee.AMAIR)?.estAnneeEnCours && (
                      <p className="mt-1 text-xs text-honey-600">
                        Votre adhésion prendra effet le 1er janvier {selectedAnnee.AMAIR}
                      </p>
                    )}
                  </div>
                  
                  {/* Vérifier si une adhésion SAR avec AMAIR gratuite est en attente pour cette année */}
                  {selectedAnnee.AMAIR && getSarAdhesionWithAMAIRGratuitePending(selectedAnnee.AMAIR) ? (
                    <div className="p-4 border-2 border-honey-400 bg-honey-50 rounded-lg">
                      <div className="flex items-start">
                        <svg className="h-5 w-5 text-honey-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-honey-800">
                            Adhésion AMAIR incluse dans votre demande SAR
                          </p>
                          <p className="text-sm text-honey-700 mt-1">
                            Votre demande d'adhésion au SAR pour {selectedAnnee.AMAIR} inclut une adhésion gratuite à l'AMAIR. 
                            Elle sera validée automatiquement une fois votre adhésion SAR confirmée.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={`/adhesion/amair?annee=${selectedAnnee.AMAIR}`}
                      className="block w-full text-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-honey-500 hover:bg-honey-600 transition-colors"
                    >
                      Adhérer à AMAIR pour {selectedAnnee.AMAIR}
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Carte Coopémiel - En cours de construction */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden opacity-75">
            <div className="p-8">
              <div className="text-center mb-6">
                <img src="/logos/logo_coopemiel.png" alt="Coopemiel" className="w-auto mx-auto h-32 mb-6" />
                <h2 className="text-2xl font-bold text-slate-900 mb-2">COOPEMIEL</h2>
                <p className="text-sm text-slate-600 font-medium">
                  Coopémiel de Bourbon
                </p>
              </div>
              
              <div className="mb-6">
                <p className="text-slate-700 text-sm leading-relaxed">
                  Coopérative apicole pour la valorisation et la commercialisation des produits de la ruche réunionnais.
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-slate-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-slate-600">Commercialisation collective</span>
                </div>
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-slate-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-slate-600">Mutualisation des moyens</span>
                </div>
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-slate-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-slate-600">Valorisation du miel réunionnais</span>
                </div>
              </div>

              {/* Badge en cours de construction */}
              <div className="flex items-center justify-center px-6 py-3 border-2 border-honey-400 rounded-lg bg-honey-50">
                <svg className="h-5 w-5 text-honey-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                <span className="text-base font-medium text-honey-700">En cours de construction</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
